<script setup lang="ts">
/**
 * Sources added from this UI (ADR 0057 Phase A): the list, and the way in.
 *
 * At rest this is one button. The section only takes space once something
 * has been added — the registry below is what most visits are for, and an
 * empty "you have no sources" box above it would be furniture.
 *
 * Status is per ASSISTANT, not per source, and the layout says so: one sync
 * walks all of an assistant's sources, so a pill on each row would be the
 * same pill repeated, claiming a precision the data does not have.
 *
 * Two states get their own wording because they are the ones that hide:
 * a sync that succeeded and found nothing (the most common way a source is
 * silently broken), and a deployment where nothing will ever run the sync.
 */
import { vWrite } from "../admin-read-only";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import {
  AdminError,
  type AssistantRow,
  type GrantedResource,
  type IngestJobRow,
  type IngestRunner,
  type ManagedSource,
  type ManagedSourceGroup,
  type SourceKind,
  deleteSource,
  getConnections,
  getManagedSources,
  getSourceTypes,
  listAssistants,
  triggerIngest,
  updateSource,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import { fmtAge, fmtDateTime } from "../utils/datetime";
import SidePanel from "./SidePanel.vue";
import SourceEditor from "./SourceEditor.vue";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{ devUser: string }>();
const toast = useToast();
const { confirm } = useConfirm();

const groups = ref<ManagedSourceGroup[]>([]);
const kinds = ref<SourceKind[]>([]);
const assistants = ref<AssistantRow[]>([]);
const resources = ref<GrantedResource[]>([]);
const runnerAvailable = ref(true);
const runner = ref<IngestRunner>("in_process");
const loaded = ref(false);

const panelOpen = ref(false);
const editing = ref<ManagedSource | null>(null);
const errorOpen = ref<Set<string>>(new Set());

let timer: ReturnType<typeof setTimeout> | null = null;

const syncing = computed(() =>
  groups.value.some(
    (g) => g.last_job?.status === "pending" || g.last_job?.status === "running",
  ),
);

async function reload(): Promise<void> {
  try {
    const res = await getManagedSources(props.devUser);
    groups.value = res.groups;
    runnerAvailable.value = res.runner_available;
    runner.value = res.runner;
  } catch (e) {
    // The tab's own load reports a broken backend; a second toast for the
    // same outage is noise. Leave the section absent.
    if (!(e instanceof AdminError)) throw e;
  } finally {
    loaded.value = true;
  }
  if (timer) clearTimeout(timer);
  // Poll only while something is moving — and gently when the wait is
  // minutes, not seconds.
  if (syncing.value)
    timer = setTimeout(reload, runner.value === "external" ? 15000 : 3000);
}

async function openAdd(): Promise<void> {
  editing.value = null;
  panelOpen.value = true;
  try {
    // Every time, not once: a site granted a minute ago has to be pickable
    // without reloading the page.
    const [types, list, conns] = await Promise.all([
      getSourceTypes(props.devUser),
      listAssistants(props.devUser),
      getConnections(props.devUser),
    ]);
    resources.value = conns.connections.flatMap((c) => c.resources);
    kinds.value = types.kinds;
    runnerAvailable.value = types.runner_available;
    runner.value = types.runner;
    assistants.value = list;
  } catch (e) {
    panelOpen.value = false;
    toast.error(
      "Could not load source types",
      e instanceof Error ? e.message : String(e),
    );
  }
}

async function openEdit(s: ManagedSource): Promise<void> {
  await openAdd();
  editing.value = s;
}

function onDone(): void {
  panelOpen.value = false;
  void reload();
}

onMounted(reload);
onBeforeUnmount(() => {
  if (timer) clearTimeout(timer);
});

// ── presentation ───────────────────────────────────────────────────────────

const KIND_LABEL: Record<string, string> = {
  website: "Website",
  url: "Web pages",
  local_files: "Files",
  sharepoint: "SharePoint",
};

function kindLabel(s: ManagedSource): string {
  return KIND_LABEL[s.source_type] ?? s.source_type;
}

/** The one thing that identifies a source to a person: where it points. */
function target(s: ManagedSource): string {
  const c = s.config as Record<string, unknown>;
  const list = (c.start_urls ??
    (Array.isArray(c.urls)
      ? (c.urls as Array<{ url?: string } | string>).map((u) =>
          typeof u === "string" ? u : (u.url ?? ""),
        )
      : null)) as string[] | null;
  if (list?.length) {
    const first = list[0].replace(/^https?:\/\//, "");
    return list.length > 1 ? `${first} +${list.length - 1} more` : first;
  }
  if (s.source_type === "local_files") return "uploaded files";
  if (s.source_type === "sharepoint") {
    const site = String(c.site_path ?? "").replace(/^\/(sites|teams)\//, "");
    const lib =
      Array.isArray(c.drives) && c.drives.length ? ` / ${c.drives[0]}` : "";
    return `${site || c.site_host}${lib}`;
  }
  return "";
}

type Pill = { tone: "ok" | "warn" | "bad" | "info" | "muted"; text: string };

function pill(job: IngestJobRow | null): Pill {
  if (!runnerAvailable.value)
    return { tone: "muted", text: "not syncing on this deployment" };
  if (!job) return { tone: "muted", text: "never synced" };
  if (job.status === "pending")
    return {
      tone: "info",
      // The indexer's Job picks the queue up on a 10-minute tick. Saying so
      // is the difference between "queued" and "is this thing broken?".
      text:
        runner.value === "external"
          ? "queued · starts within 10 min"
          : "queued",
    };
  if (job.status === "running") {
    const n = job.docs_indexed + job.docs_skipped;
    return { tone: "info", text: n ? `syncing · ${n} documents` : "syncing" };
  }
  const when = fmtAge(job.finished_at ?? job.created_at);
  if (job.status === "failed") return { tone: "bad", text: `failed ${when}` };
  const n = job.docs_indexed + job.docs_skipped;
  if (n === 0) return { tone: "warn", text: `synced ${when} · found nothing` };
  return {
    tone: "ok",
    text: `synced ${when} · ${n} ${n === 1 ? "document" : "documents"}`,
  };
}

function toggleError(slug: string): void {
  if (errorOpen.value.has(slug)) errorOpen.value.delete(slug);
  else errorOpen.value.add(slug);
  errorOpen.value = new Set(errorOpen.value);
}

// ── actions ────────────────────────────────────────────────────────────────

async function syncNow(g: ManagedSourceGroup): Promise<void> {
  try {
    await triggerIngest(g.assistant_slug, props.devUser);
    await reload();
  } catch (e) {
    toast.error(
      "Could not start sync",
      e instanceof Error ? e.message : String(e),
    );
  }
}

async function setEnabled(s: ManagedSource, enabled: boolean): Promise<void> {
  try {
    await updateSource(s.assistant_slug, s.id, { enabled }, props.devUser);
    await reload();
  } catch (e) {
    toast.error(
      "Could not update source",
      e instanceof Error ? e.message : String(e),
    );
  }
}

async function remove(s: ManagedSource): Promise<void> {
  const ok = await confirm({
    title: "Remove this source?",
    message:
      `${kindLabel(s)} “${target(s)}” will be removed from ${s.assistant_slug}, ` +
      "along with everything it indexed. The assistant stops finding that " +
      "content straight away. To stop syncing but keep the content, pause it instead.",
    confirmLabel: "Remove",
    danger: true,
  });
  if (!ok) return;
  try {
    const res = await deleteSource(s.assistant_slug, s.id, props.devUser);
    toast.success(
      "Source removed",
      res.chunks_purged
        ? `${res.chunks_purged} indexed passages deleted.`
        : undefined,
    );
    await reload();
  } catch (e) {
    // A failed purge keeps the source on purpose — say so, it is retryable.
    toast.error(
      "Could not remove source",
      e instanceof Error ? e.message : String(e),
    );
  }
}

defineExpose({ openAdd });
</script>

<template>
  <section v-if="loaded && groups.length" class="managed">
    <h3 class="managed__title">Added here</h3>
    <p v-if="!runnerAvailable" class="notice">
      These are saved but <strong>not syncing on this deployment</strong> — the
      job that runs them is not deployed here
      (<code>INCLUDE_DB_SOURCES_CRON</code>).
    </p>

    <div v-for="g in groups" :key="g.assistant_slug" class="group">
      <div class="group__head">
        <span class="group__name">{{ g.assistant_name }}</span>
        <StatusChip
          :tone="pill(g.last_job).tone"
          dot
          :title="
            fmtDateTime(g.last_job?.finished_at ?? g.last_job?.created_at)
          "
        >
          {{ pill(g.last_job).text }}
        </StatusChip>
        <button
          v-if="g.last_job?.last_error"
          class="link"
          :aria-expanded="errorOpen.has(g.assistant_slug)"
          @click="toggleError(g.assistant_slug)"
        >
          {{ errorOpen.has(g.assistant_slug) ? "hide detail" : "why?" }}
        </button>
        <span class="spacer" />
        <button
          v-if="runnerAvailable"
          class="link"
          :disabled="
            g.last_job?.status === 'pending' || g.last_job?.status === 'running'
          "
          v-write
          @click="syncNow(g)"
        >
          sync now
        </button>
      </div>
      <pre v-if="errorOpen.has(g.assistant_slug)" class="group__error">{{
        g.last_job?.last_error
      }}</pre>

      <ul class="rows">
        <li v-for="s in g.sources" :key="s.id" class="row">
          <div class="row__main">
            <span class="row__kind">{{ kindLabel(s) }}</span>
            <span class="row__target">{{ target(s) }}</span>
            <StatusChip v-if="!s.enabled" tone="muted">paused</StatusChip>
          </div>
          <div class="row__actions">
            <button v-write v-if="s.form" class="link" @click="openEdit(s)">
              edit
            </button>
            <button v-write class="link" @click="setEnabled(s, !s.enabled)">
              {{ s.enabled ? "pause" : "resume" }}
            </button>
            <button v-write class="link link--danger" @click="remove(s)">remove</button>
          </div>
          <p v-for="(w, i) in s.warnings" :key="i" class="row__warn">{{ w }}</p>
        </li>
      </ul>
    </div>
  </section>

  <SidePanel
    v-model="panelOpen"
    :title="editing ? 'Edit source' : 'Add a source'"
    :subtitle="
      editing ? `${kindLabel(editing)} · ${editing.assistant_slug}` : ''
    "
    width="min(38rem, 92vw)"
  >
    <SourceEditor
      v-if="panelOpen && kinds.length"
      :dev-user="devUser"
      :kinds="kinds"
      :assistants="assistants"
      :runner-available="runnerAvailable"
      :runner="runner"
      :resources="resources"
      :editing="editing"
      @done="onDone"
    />
  </SidePanel>
</template>

<style scoped>
.managed {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.managed__title {
  margin: 0;
  font-size: 0.95rem;
}
.notice {
  margin: 0;
  padding: 0.5rem 0.7rem;
  font-size: 0.85rem;
  color: var(--color-warn-ink);
  background: var(--color-warn-bg);
  border-radius: var(--radius-sm);
}
.group {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}
.group__head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.75rem;
}
.group__name {
  font-weight: 650;
}
.spacer {
  flex: 1;
}
.group__error {
  margin: 0 0.75rem 0.5rem;
  padding: 0.5rem;
  font-family: var(--font-mono, monospace);
  font-size: 0.8rem;
  white-space: pre-wrap;
  background: var(--color-surface-alt);
  border-radius: var(--radius-sm);
}
.rows {
  list-style: none;
  margin: 0;
  padding: 0;
}
.row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.2rem 0.75rem;
  padding: 0.45rem 0.75rem;
  border-top: 1px solid var(--color-border);
}
.row__main {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  min-width: 0;
}
.row__kind {
  font-size: 0.8rem;
  color: var(--color-muted);
  white-space: nowrap;
}
.row__target {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.row__actions {
  display: flex;
  gap: 0.75rem;
}
.row__warn {
  grid-column: 1 / -1;
  margin: 0;
  font-size: 0.82rem;
  color: var(--color-warn-ink);
}
button.link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.85rem;
  color: var(--color-accent);
  cursor: pointer;
}
button.link:disabled {
  color: var(--color-muted);
  cursor: default;
}
button.link.link--danger {
  color: var(--color-error);
}
</style>
